import axios from 'axios'
import { SUCCESS_CODE } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { isIdleExemptRoute, isIdleExpired } from '@/utils/idleTimeout'

const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH

/** 业务码：登录 token 失效（与后端 BusinessExceptionType.LOGIN_TOKEN_ERROR 一致） */
export const LOGIN_TOKEN_ERROR_CODE = 1214

let refreshPromise: Promise<string | null> | null = null

/** 解析 JWT payload 中的过期时间（毫秒时间戳） */
export const parseJwtExp = (token: string): number | null => {
  try {
    const payload = token.split('.')[1]
    if (!payload) return null
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = JSON.parse(atob(normalized))
    return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null
  } catch {
    return null
  }
}

/** accessToken 是否已过期（默认提前 60 秒视为过期，避免临界请求失败） */
export const isAccessTokenExpired = (token: string, skewMs = 60_000): boolean => {
  if (!token) return true
  const exp = parseJwtExp(token)
  if (!exp) return false
  return Date.now() >= exp - skewMs
}

/** 判断业务响应是否表示登录态失效（仅业务码/文案；HTTP 403 权限不足不算登录失效） */
export const isBusinessLoginExpired = (data: { code?: number; message?: string } | null | undefined): boolean => {
  if (!data) return false
  return (
    data.code === 401 ||
    data.code === LOGIN_TOKEN_ERROR_CODE ||
    data.message === 'Unauthorized' ||
    data.message === '登录已失效' ||
    data.message === '登录异常' ||
    data.message === '需要登录'
  )
}

/**
 * 使用 refreshToken 换取新的 accessToken（单飞锁，避免并发重复刷新）
 * @returns 新 accessToken；刷新失败返回 null
 */
export const refreshAccessToken = async (): Promise<string | null> => {
  // 空闲超时后禁止用 refreshToken 续期（登录页 / SSO 除外）
  if (!isIdleExemptRoute() && isIdleExpired()) {
    return null
  }

  if (refreshPromise) {
    return refreshPromise
  }

  const userStore = useUserStoreWithOut()
  const refreshToken = userStore.getRefreshToken
  const accessToken = userStore.getToken

  if (!refreshToken) {
    return null
  }

  refreshPromise = (async () => {
    try {
      const res = await axios.post(
        `${API_BASE_PATH}/auth/refresh`,
        { accessToken, refreshToken },
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Client-Type': 'web'
          }
        }
      )
      const body = res.data
      if (body?.code === SUCCESS_CODE && body?.data?.accessToken) {
        userStore.setToken(body.data.accessToken)
        if (body.data.refreshToken) {
          userStore.setRefreshToken(body.data.refreshToken)
        }
        return body.data.accessToken as string
      }
      return null
    } catch {
      return null
    } finally {
      refreshPromise = null
    }
  })()

  return refreshPromise
}

/**
 * accessToken 过期时尝试静默续期
 * @returns 可用于请求的 token；续期失败返回 null
 */
export const ensureValidAccessToken = async (): Promise<string | null> => {
  // 空闲超时：不续期，交由请求拦截器触发退出
  if (!isIdleExemptRoute() && isIdleExpired()) {
    return null
  }
  const userStore = useUserStoreWithOut()
  const current = userStore.getToken
  if (!current) return null
  if (!isAccessTokenExpired(current)) return current
  return refreshAccessToken()
}
